import BillingTransactionsService from 'js/services/api/billing/transactions.js'

import MediaPanelsCommonMixin from '../balance/account/_media_panels_common_mixin'

import template from './_account-summary.pug'

const QR_MEDIA_TYPE = '2'
const TNE_MEDIA_TYPE = '4'

const TYPE_CHARGE = 5
const TYPE_REFUND = 7
const TYPE_VALIDATION = 8

const VALIDATION_TYPE_VALIDATION = 1
const VALIDATION_TYPE_HOP_ON_HOP_OFF = 2
const VALIDATION_TYPE_PENALIZATION = 3
const DEFAULT_VISIBLE_TRANSACTIONS = 3

// @vue/component
export default {
  name: 'new-balance-account-summary',
  template: template(),

  mixins: [MediaPanelsCommonMixin],

  props: {
    account: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      transactions: [],
      loadingTransactions: false,
      movementsExpanded: true,
      visibleTransactionsCount: DEFAULT_VISIBLE_TRANSACTIONS,
      pagination: {
        currentPage: 1,
        limit: 10,
        totalElements: 0
      }
    }
  },

  computed: {
    balance() {
      return this.getBalance(this.account)
    },

    accountName() {
      if (!this.account.mediaType) { return '' }

      return this.$t(`balance.mediaTypes.${this.account.mediaType}`)
    },

    profileName() {
      const profileName = this.mediaProfiles[this.account.profile]

      if (profileName === 'Public General') {
        return this.$t('balance.accountBalanceView.publicGeneral')
      }

      return profileName || '-'
    },

    hasTransactions() {
      return this.transactions && this.transactions.length > 0
    },

    visibleTransactions() {
      return this.transactions.slice(0, this.visibleTransactionsCount)
    },

    hasMoreTransactions() {
      return this.transactions.length > this.visibleTransactions.length
    },

    isShowingAllTransactions() {
      return this.hasTransactions && this.visibleTransactionsCount >= this.transactions.length
    },

    transactionsAmounts() {
      return this.transactions.map(transaction => this.getTransactionAmount(transaction) * -1)
    },

    latestMovementDate() {
      if (!this.hasTransactions) { return '-' }

      return this.transactions[0].date || '-'
    }
  },

  watch: {
    account() {
      this.pagination.currentPage = 1
      this.visibleTransactionsCount = DEFAULT_VISIBLE_TRANSACTIONS
      this.getQrTransactions()
    },

    '$route'() {
      this.getQrTransactions()
    }
  },

  created() {
    this.getQrTransactions()
  },

  methods: {
    getQrTransactions() {
      if (!this.account || !this.account.mediaCode) { return }

      const params = {
        administrative_area_id: this.$administrativeArea.id,
        media_type_array: [QR_MEDIA_TYPE, TNE_MEDIA_TYPE],
        account_client_transactions: true,
        order_by: 'operation_date',
        order_dir: 'desc',
        per: 50
      }

      this.loadingTransactions = true

      BillingTransactionsService.index(params).then(data => {
        const transactions = this.prepareQrTransactions(data)
        const firstPosition = (this.pagination.currentPage - 1) * this.pagination.limit

        this.visibleTransactionsCount = DEFAULT_VISIBLE_TRANSACTIONS
        this.pagination.totalElements = transactions.length
        this.transactions = transactions.slice(
          firstPosition,
          firstPosition + this.pagination.limit
        )
      }).finally(() => {
        this.loadingTransactions = false
      })
    },

    prepareQrTransactions(transactions) {
      const timeZone = this.$administrativeAreaTimeZone

      if (timeZone) {
        transactions.forEach(transaction => {
          if (transaction.operation_date) {
            const date = moment(transaction.operation_date).tz(timeZone)
            transaction.date = date.format('DD MMM HH:mm').replace('.', '')
          }
        })
      }

      return transactions
    },

    changeLimit(limit) {
      this.pagination.limit = limit
      this.pagination.currentPage = 1
      this.getQrTransactions()
    },

    changePage(page) {
      this.pagination.currentPage = page
      this.getQrTransactions()
    },

    isCharge(transaction) {
      return transaction.type === TYPE_CHARGE
    },

    isRefund(transaction) {
      return transaction.type === TYPE_REFUND
    },

    isValidation(transaction) {
      return transaction.type === TYPE_VALIDATION
    },

    transactionTitle(transaction) {
      if (this.isCharge(transaction)) { return this.$t('balance.transaction.type.recharge') }
      if (this.isRefund(transaction)) { return this.$t('balance.transaction.type.refund') }
      if (this.isValidation(transaction)) {
        const title = this.validationTypeName(transaction)

        return this.isTransfer(transaction) ?
          `${title} ${this.$t('balance.accountBalanceView.transfer')}` :
          title
      }

      return this.$t(`balance.transaction.type.${transaction.type}`)
    },

    validationTypeName(transaction) {
      const names = {}
      names[VALIDATION_TYPE_VALIDATION] = this.$t('balance.transaction.type.validation')
      names[VALIDATION_TYPE_HOP_ON_HOP_OFF] = this.$t('balance.transaction.type.hopOnHopOff')
      names[VALIDATION_TYPE_PENALIZATION] = this.$t('balance.transaction.type.penalization')

      return names[transaction.validation_type] || this.$t('balance.transaction.travel')
    },

    isTransfer(transaction) {
      const relatedTransaction = this.relatedAccountTransaction(transaction) || {}

      return [
        transaction.is_transfer,
        transaction.isTransfer,
        relatedTransaction.is_transfer,
        relatedTransaction.isTransfer
      ]
        .some(value => value === true || value === 1 || value === '1' || value === 'true')
    },

    relatedAccountTransaction(transaction) {
      const transactions = this.account.transactions || []
      const transactionId = transaction.id || _.get(transaction, '_id')

      if (!transactionId) { return null }

      return transactions.find(item => {
        const itemId = item.id || _.get(item, '_id')

        return itemId === transactionId
      }) || null
    },

    transactionAmount(transaction) {
      if (this.isCharge(transaction)) { return transaction.paid_amount }
      if (this.isValidation(transaction)) { return transaction.amount || 0 }

      return this.getTransactionAmount(transaction)
    },

    travelAmount(transaction) {
      return transaction.amount || 0
    },

    transactionAmountClass(transaction) {
      return this.transactionAmount(transaction) < 0 ? 'is-negative' : 'is-positive'
    },

    mediaTypeName(transaction) {
      if (!transaction.media_type) { return '-' }

      return this.$t(`balance.transaction.mediaTypes.${transaction.media_type}`)
    },

    routeName(transaction) {
      if (!transaction.route) { return '-' }

      return transaction.route.short_name || transaction.route.name || '-'
    },

    routeColor(transaction) {
      const route = transaction.route || {}

      return `background-color: ${route.color || '#1f7a4d'};`
    },

    stopName(transaction) {
      if (!transaction.stop) { return '-' }

      if (transaction.stop_to) {
        return `${transaction.stop.name || '-'} / ${transaction.stop_to.name || '-'}`
      }

      return transaction.stop.name || '-'
    },

    routeDescription(transaction) {
      const route = transaction.route || {}

      return route.origin && route.destination ?
        `${route.origin} - ${route.destination}` :
        route.description2
    },

    crossedAreasText(transaction) {
      return transaction.crossed_areas ?
        `${this.$t('balance.accountBalanceView.zone')} ${transaction.crossed_areas}` :
        ''
    },

    isCapping(transaction) {
      return transaction?.capping_applied === true ||
        (!_.isNil(transaction?.capping_limit) && !_.isNil(transaction?.capping_sum))
    },

    cappingZone(transaction) {
      return transaction?.crossed_areas || '-'
    },

    cappingFareText(transaction) {
      if (!this.isCapping(transaction)) { return '' }

      return this.$t('balance.accountBalanceView.bestFareZone', {
        zone: this.cappingZone(transaction)
      })
    },

    cappingMessage(transaction) {
      if (!this.isCapping(transaction)) { return '' }

      return this.$t('balance.accountBalanceView.monthlyLimitReachedZone', {
        zone: this.cappingZone(transaction)
      })
    },

    assumedBalance(index) {
      const accumulatedAmounts = this.transactionsAmounts
        .slice(0, index)
        .reduce((a, b) => a + b, 0)

      return this.balance + accumulatedAmounts
    },

    showMoreTransactions() {
      this.visibleTransactionsCount = this.transactions.length
    },

    showLessTransactions() {
      this.visibleTransactionsCount = DEFAULT_VISIBLE_TRANSACTIONS
    },

    toggleMovements() {
      this.movementsExpanded = !this.movementsExpanded
    },
    bonusAmount(transaction) {
      console.log("transaction", transaction)
      if (!this.isCharge(transaction)) {
        return 0
      }
      console.log(this.isCharge(transaction))
      const caca = (transaction.recharge_amount || 0) -
        (transaction.paid_amount || 0)
      console.log(caca)
      return (
        (transaction.recharge_amount || 0) -
        (transaction.paid_amount || 0)
      )
    }
  }
}
